import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Syringe, Baby, AlertTriangle, CalendarClock, Scale, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { Kpi, Section, Empty, Pill, PageTitle, fmtDate } from "@/components/services/Shared";

export default function Pediatrie() {
  const { user } = useApp();
  const canCare = ["hospital_admin", "doctor", "nurse"].includes(user?.role);
  const [data, setData] = useState(null);
  const [child, setChild] = useState(null);       // detailed record
  const [vacc, setVacc] = useState(null);         // {patient}
  const [growth, setGrowth] = useState(null);
  const [vf, setVf] = useState({ vaccine: "bcg", date: "", next_due: "", batch: "", notes: "" });
  const [gf, setGf] = useState({ date: "", weight_kg: "", height_cm: "", muac_mm: "", notes: "" });
  const today = new Date().toISOString().slice(0, 10);

  const load = () => axios.get(`${API}/services/pediatrie`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);
  const openChild = (pid) => axios.get(`${API}/services/pediatrie/${pid}`).then((r) => setChild(r.data)).catch(() => toast.error("Dossier introuvable"));
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const post = async (url, body, ok, after) => {
    try { await axios.post(url, body); toast.success(ok); after(); load(); if (child) openChild(child.patient.id); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <div className="text-[#5C6B61]">Chargement…</div>;
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="pediatrie-page">
      <PageTitle icon={Syringe} title="Service de Pédiatrie" subtitle="Enfants 0-3 ans : calendrier vaccinal PEV, croissance et dépistage de la malnutrition." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Enfants suivis" value={s.children} icon={Baby} color="#1E4631" sub={`${s.fully_started} avec vaccination démarrée`} testid="kpi-children" />
        <Kpi label="Vaccins administrés" value={s.vaccinations_total} icon={Syringe} color="#2E7D32" testid="kpi-vaccs" />
        <Kpi label="Rappels à venir (30 j)" value={s.upcoming_30d} icon={CalendarClock} color="#E3A72F" sub={s.overdue ? `${s.overdue} en retard` : undefined} testid="kpi-upcoming" />
        <Kpi label="Alertes malnutrition (PB < 125 mm)" value={s.malnutrition_flags} icon={AlertTriangle} color="#C62828" testid="kpi-malnutrition" />
      </div>

      {data.overdue.length > 0 && (
        <Section title={`Vaccins en retard (${data.overdue.length})`} icon={AlertTriangle} testid="overdue-section">
          <div className="divide-y divide-[#E5EBE7]">
            {data.overdue.map((v) => (
              <div key={v.id} className="py-2 flex items-center justify-between text-sm">
                <span><strong>{v.patient_name}</strong> · prochain : {v.vaccine_label} prévu le <span className="text-[#C62828] font-semibold">{fmtDate(v.next_due)}</span></span>
                <Button size="sm" variant="outline" onClick={() => openChild(v.patient_id)}>Dossier</Button>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Enfants 0-3 ans" icon={Baby} testid="children-section">
        {data.children.length === 0 ? <Empty>Aucun enfant enregistré. Ajoutez un patient de catégorie "Enfant 0-3 ans".</Empty> : (
          <div className="divide-y divide-[#E5EBE7]">
            {data.children.map((c) => (
              <div key={c.id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`child-${c.id}`}>
                <div className="flex-1 min-w-[200px]"><span className="font-medium">{c.name}</span> <span className="text-xs text-[#5C6B61]">· {c.age} an(s) · {c.phone}{c.commune ? ` · ${c.commune}` : ""}</span></div>
                <Pill color={c.vaccinations ? "#2E7D32" : "#E3A72F"}>{c.vaccinations} vaccin(s)</Pill>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openChild(c.id)} data-testid={`child-open-${c.id}`}>Carnet</Button>
                  {canCare && <Button size="sm" className="bg-[#2E7D32] text-white" onClick={() => { setVacc(c); setVf({ vaccine: "bcg", date: today, next_due: "", batch: "", notes: "" }); }} data-testid={`child-vacc-${c.id}`}><Syringe className="w-4 h-4 mr-1" /> Vacciner</Button>}
                  {canCare && <Button size="sm" variant="outline" onClick={() => { setGrowth(c); setGf({ date: today, weight_kg: "", height_cm: "", muac_mm: "", notes: "" }); }} data-testid={`child-growth-${c.id}`}><Scale className="w-4 h-4 mr-1" /> Mesures</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Calendrier vaccinal PEV (RDC)" icon={CalendarClock}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
          {data.pev_calendar.map((v) => (
            <div key={v.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#FDFBF7] border border-[#E5EBE7]"><span>{v.label}</span><span className="text-xs text-[#5C6B61]">{v.age}</span></div>
          ))}
        </div>
      </Section>

      {/* Carnet enfant */}
      <Dialog open={!!child} onOpenChange={(v) => !v && setChild(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Carnet de santé — {child?.patient?.name}</DialogTitle></DialogHeader>
          {child && (
            <div className="space-y-5" data-testid="child-record">
              <div>
                <h3 className="text-sm font-semibold text-[#1E4631] mb-2">Statut vaccinal PEV</h3>
                <div className="grid sm:grid-cols-2 gap-1.5 text-sm">
                  {child.pev_status.map((v) => (
                    <div key={v.id} className="flex items-center gap-2">
                      {v.done ? <CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> : <Circle className="w-4 h-4 text-[#E5EBE7]" />}
                      <span className={v.done ? "" : "text-[#5C6B61]"}>{v.label}</span><span className="text-xs text-[#8A9990]">({v.age})</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#1E4631] mb-2">Historique des vaccins ({child.vaccinations.length})</h3>
                {child.vaccinations.length === 0 ? <Empty>Aucun vaccin enregistré.</Empty> : (
                  <table className="w-full text-sm"><tbody className="divide-y divide-[#E5EBE7]">
                    {child.vaccinations.map((v) => <tr key={v.id}><td className="py-1.5">{v.vaccine_label}</td><td>{fmtDate(v.date)}</td><td className="text-xs text-[#5C6B61]">{v.next_due ? `Rappel ${fmtDate(v.next_due)}` : ""}</td><td className="text-xs text-[#5C6B61]">{v.by}</td></tr>)}
                  </tbody></table>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#1E4631] mb-2">Croissance ({child.growth.length})</h3>
                {child.growth.length === 0 ? <Empty>Aucune mesure.</Empty> : (
                  <table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase text-[#5C6B61]"><th>Date</th><th>Poids</th><th>Taille</th><th>PB</th><th></th></tr></thead><tbody className="divide-y divide-[#E5EBE7]">
                    {child.growth.map((g) => <tr key={g.id}><td className="py-1.5">{fmtDate(g.date)}</td><td>{g.weight_kg ?? "—"} kg</td><td>{g.height_cm ?? "—"} cm</td><td>{g.muac_mm ?? "—"} mm</td><td>{g.malnutrition_flag && <Pill color="#C62828">Malnutrition</Pill>}</td></tr>)}
                  </tbody></table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vaccination */}
      <Dialog open={!!vacc} onOpenChange={(v) => !v && setVacc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vaccination — {vacc?.name}</DialogTitle></DialogHeader>
          <form className="space-y-3" data-testid="vacc-form" onSubmit={(e) => { e.preventDefault(); post(`${API}/services/pediatrie/vaccinations`, { patient_id: vacc.id, ...vf, next_due: vf.next_due || null }, "Vaccin enregistré", () => setVacc(null)); }}>
            <div><Label>Vaccin</Label>
              <Select value={vf.vaccine} onValueChange={(v) => setVf({ ...vf, vaccine: v })}><SelectTrigger data-testid="vacc-vaccine"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{data.pev_calendar.map((v) => <SelectItem key={v.id} value={v.id}>{v.label} — {v.age}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={vf.date} onChange={(e) => setVf({ ...vf, date: e.target.value })} data-testid="vacc-date" /></div>
              <div><Label>Prochain rappel</Label><Input type="date" value={vf.next_due} onChange={(e) => setVf({ ...vf, next_due: e.target.value })} data-testid="vacc-next" /></div>
            </div>
            <div><Label>N° de lot</Label><Input value={vf.batch} onChange={(e) => setVf({ ...vf, batch: e.target.value })} data-testid="vacc-batch" /></div>
            <div><Label>Notes</Label><Textarea value={vf.notes} onChange={(e) => setVf({ ...vf, notes: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setVacc(null)}>Annuler</Button><Button type="submit" className="bg-[#2E7D32] text-white" data-testid="vacc-save">Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Growth */}
      <Dialog open={!!growth} onOpenChange={(v) => !v && setGrowth(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mesures de croissance — {growth?.name}</DialogTitle></DialogHeader>
          <form className="space-y-3" data-testid="growth-form" onSubmit={(e) => { e.preventDefault(); post(`${API}/services/pediatrie/growth`, { patient_id: growth.id, ...gf, weight_kg: num(gf.weight_kg), height_cm: num(gf.height_cm), muac_mm: num(gf.muac_mm) }, "Mesures enregistrées", () => setGrowth(null)); }}>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={gf.date} onChange={(e) => setGf({ ...gf, date: e.target.value })} data-testid="growth-date" /></div>
              <div><Label>Poids (kg)</Label><Input type="number" step="0.1" value={gf.weight_kg} onChange={(e) => setGf({ ...gf, weight_kg: e.target.value })} data-testid="growth-weight" /></div>
              <div><Label>Taille (cm)</Label><Input type="number" step="0.5" value={gf.height_cm} onChange={(e) => setGf({ ...gf, height_cm: e.target.value })} data-testid="growth-height" /></div>
              <div><Label>Périmètre brachial (mm)</Label><Input type="number" value={gf.muac_mm} onChange={(e) => setGf({ ...gf, muac_mm: e.target.value })} data-testid="growth-muac" /></div>
            </div>
            <p className="text-xs text-[#5C6B61]">PB &lt; 125 mm = malnutrition aiguë modérée · &lt; 115 mm = sévère (référence OMS, 6-59 mois).</p>
            <div><Label>Notes</Label><Textarea value={gf.notes} onChange={(e) => setGf({ ...gf, notes: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setGrowth(null)}>Annuler</Button><Button type="submit" className="bg-[#1E4631] text-white" data-testid="growth-save">Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
